import { SseParser } from './sse-parser';

/** On-wire shape of every message on the per-user channel; consumers narrow `payload` by `type`. */
export interface SseEnvelope {
  type: string;
  aggregateId?: string;
  payload: unknown;
  ts: string;
}

export class SseHttpError extends Error {
  public readonly status: number;

  public constructor(status: number) {
    super(`SSE connect failed with HTTP ${status}`);
    this.name = 'SseHttpError';
    this.status = status;
  }
}

export class SseIdleTimeoutError extends Error {
  public constructor(ms: number) {
    super(`SSE stream sent nothing for ${ms}ms`);
    this.name = 'SseIdleTimeoutError';
  }
}

export interface SseConnectionOptions {
  url: string;
  /** Called before every connect, so a refreshed token is picked up on reconnect. */
  getHeaders: () => Promise<Record<string, string>>;
  onEvent: (envelope: SseEnvelope) => void;
  /** Fires once a connect delivers its first bytes. Events can be missed while disconnected, so refetch here. */
  onOpen?: () => void;
  /** Fires when an open stream ends or fails, before the reconnect is scheduled. */
  onClose?: () => void;
  onError?: (error: unknown) => void;
  fetchFn?: typeof fetch;
  initialRetryMs?: number;
  maxRetryMs?: number;
}

export interface SseConnection {
  close: () => void;
}

const DEFAULT_RETRY_MS = 1000;
const DEFAULT_MAX_RETRY_MS = 30_000;
const JITTER = 0.2;
// The server sends a heartbeat every 25s, so silence past two of them means the
// connection died without the browser being told (a network switch, a dropped NAT entry).
const IDLE_TIMEOUT_MS = 60_000;
// The server preempts a healthy stream after about four minutes. One that stops delivering
// before outliving a heartbeat was refused, e.g. during a Redis outage, so it counts as a failure.
const STABLE_AFTER_MS = 30_000;
// setTimeout overflows above this and fires immediately.
const MAX_TIMER_DELAY_MS = 2_147_483_647;

// Spread reconnects so a fleet-wide drop, such as a deploy, does not come back as one spike.
const withJitter = (ms: number): number => ms * (1 + (Math.random() * 2 - 1) * JITTER);

/**
 * Fetch-based SSE client. `EventSource` cannot send the Authorization header our
 * backend requires, so this reads the body stream directly and owns reconnection.
 * The server preempts every stream after a few minutes and sends a `retry:` hint
 * for exactly that; failures back off exponentially up to `maxRetryMs`.
 */
export const connectSse = (options: SseConnectionOptions): SseConnection => {
  const fetchFn = options.fetchFn ?? globalThis.fetch;
  let closed = false;
  let retryMs = options.initialRetryMs ?? DEFAULT_RETRY_MS;
  let consecutiveFailures = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let round: AbortController | undefined;

  const schedule = (delayMs: number) => {
    if (closed) {
      return;
    }
    timer = setTimeout(() => void run(), Math.min(withJitter(delayMs), MAX_TIMER_DELAY_MS));
  };

  const backOff = () => {
    consecutiveFailures += 1;
    // The server's hint may be 0; backing off from that would retry in a tight loop.
    const base = Math.max(retryMs, options.initialRetryMs ?? DEFAULT_RETRY_MS);
    schedule(Math.min(base * 2 ** consecutiveFailures, options.maxRetryMs ?? DEFAULT_MAX_RETRY_MS));
  };

  const deliver = (data: string) => {
    let envelope: unknown;
    try {
      envelope = JSON.parse(data);
    } catch {
      return;
    }
    if (typeof envelope === 'object' && envelope !== null && typeof (envelope as SseEnvelope).type === 'string') {
      options.onEvent(envelope as SseEnvelope);
    }
  };

  const run = async () => {
    if (closed) {
      return;
    }
    const current = new AbortController();
    round = current;
    let idle: ReturnType<typeof setTimeout> | undefined;
    const keepAlive = () => {
      clearTimeout(idle);
      idle = setTimeout(() => current.abort(new SseIdleTimeoutError(IDLE_TIMEOUT_MS)), IDLE_TIMEOUT_MS);
    };
    // Open means bytes arrived: a proxy that buffers the stream still answers 200 at once.
    let openedAt: number | undefined;
    let lastChunkAt = 0;
    const lasted = () => openedAt !== undefined && lastChunkAt - openedAt >= STABLE_AFTER_MS;
    try {
      const headers = await options.getHeaders();
      current.signal.throwIfAborted();
      keepAlive();
      const response = await fetchFn(options.url, {
        headers: { ...headers, Accept: 'text/event-stream' },
        signal: current.signal,
        credentials: 'include',
        cache: 'no-store',
      });
      if (!response.ok || !response.body) {
        throw new SseHttpError(response.status);
      }

      const reader = response.body.getReader();
      // Fetch rejects a pending read on abort; cancelling covers bodies that ignore the signal.
      current.signal.addEventListener('abort', () => void reader.cancel().catch(() => undefined));
      const decoder = new TextDecoder();
      const parser = new SseParser();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        keepAlive();
        lastChunkAt = Date.now();
        if (openedAt === undefined) {
          openedAt = lastChunkAt;
          options.onOpen?.();
        }
        for (const frame of parser.feed(decoder.decode(value, { stream: true }))) {
          if ('retryMs' in frame) {
            retryMs = frame.retryMs;
          } else {
            deliver(frame.data);
          }
        }
      }
      current.signal.throwIfAborted();

      if (lasted()) {
        // A clean end after a healthy stream is the server's preempt; come back after its own hint.
        consecutiveFailures = 0;
        schedule(retryMs);
      } else {
        backOff();
      }
      if (openedAt !== undefined) {
        options.onClose?.();
      }
    } catch (error) {
      if (closed) {
        return;
      }
      if (lasted()) {
        consecutiveFailures = 0;
      }
      // Scheduled before the callbacks, so one that throws cannot stop reconnecting.
      backOff();
      if (openedAt !== undefined) {
        options.onClose?.();
      }
      options.onError?.(current.signal.aborted ? current.signal.reason : error);
    } finally {
      clearTimeout(idle);
      // Releases the connection if a callback threw mid-stream; a no-op once it has ended.
      current.abort();
    }
  };

  void run();

  return {
    close: () => {
      closed = true;
      clearTimeout(timer);
      round?.abort();
    },
  };
};
